import React, { Children, Component } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, RefreshCw, Sparkles, Trophy } from 'lucide-react';
import { type ModuleId, getModuleColors, getModuleIcon } from '../../content';
import { DjayAvatar, type DjayVariant, type DjayEmotion, type DjayAccessory, type DjayTier } from '../../mascot';
import type { SmartSessions, SmartSessionKind, SmartSessionSegment } from '../../mastery';
/**
 * SmartSessionCard (F4.1) — single primary card on HomePage that consolidates
 * "Continue", "Djay raadt aan" and "Herhaal-tip" into three explicit choices:
 *
 *   • Herhalen        — review what's slipping
 *   • Nieuw           — try a fresh module
 *   • Bijna Expert    — finish what's almost mastered
 *
 * One segment is highlighted as Djay's pick. Children of 9-12 still see three
 * options, but the visual hierarchy makes the recommended one obvious.
 */
interface SmartSessionCardProps {
  sessions: SmartSessions;
  djayVariant?: DjayVariant;
  djayAccessory?: DjayAccessory;
  djayTier?: DjayTier;
  onStart: (moduleId: ModuleId) => void;
}
const SEGMENT_META: Record<SmartSessionKind, {
  label: string;
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  accent: string;
  djayEmotion: DjayEmotion;
  djayLine: (segment: SmartSessionSegment) => string;
}> = {
  review: {
    label: 'Herhalen',
    icon: RefreshCw,
    accent: 'bg-teal-100 text-teal-600',
    djayEmotion: 'thinking',
    djayLine: s => `Tijd voor een opfrisser bij ${s.module.title}.`
  },
  almostExpert: {
    label: 'Bijna expert',
    icon: Trophy,
    accent: 'bg-amber-100 text-amber-600',
    djayEmotion: 'celebrating',
    djayLine: s => `${s.module.title} ben je bijna meester — afmaken?`
  },
  fresh: {
    label: 'Iets nieuws',
    icon: Sparkles,
    accent: 'bg-indigo-100 text-indigo-600',
    djayEmotion: 'pointing',
    djayLine: s => `Klaar voor iets nieuws? Probeer ${s.module.title}.`
  }
};
const ORDER: SmartSessionKind[] = ['review', 'almostExpert', 'fresh'];
export function SmartSessionCard({
  sessions,
  djayVariant = 'default',
  djayAccessory,
  djayTier,
  onStart
}: SmartSessionCardProps) {
  const segments = ORDER.map(kind => sessions[kind]).filter((s): s is SmartSessionSegment => Boolean(s));
  if (segments.length === 0) return null;
  const primaryKind: SmartSessionKind = sessions.primary ?? segments[0].kind;
  const primary = sessions[primaryKind] ?? segments[0];
  const primaryMeta = SEGMENT_META[primary.kind];
  const primaryColors = getModuleColors(primary.module.color);
  const PrimaryIcon = getModuleIcon(primary.module.icon);
  const others = segments.filter(s => s.kind !== primary.kind);
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className="overflow-hidden rounded-[2rem] shadow-lg" data-id="element-1488" data-mp="2mmbsb9ewkx0k">
      
      {/* ── Hero: Djay's recommended pick ─────────────────────────────── */}
      <button type="button" onClick={() => onStart(primary.module.id)} className={`relative block w-full overflow-hidden bg-gradient-to-br ${primaryColors.from} ${primaryColors.to} p-5 text-left text-white transition-transform hover:scale-[1.005] active:scale-[0.995] sm:p-6`} data-id="element-1489" data-mp="1y74ic1b2c0z">
        
        <div className="pointer-events-none absolute right-0 top-0 h-48 w-48 -translate-y-1/2 translate-x-1/4 rounded-full bg-white/20 blur-3xl" data-id="element-1490" data-mp="3vooiv6gtkflj" />

        {/* Top row: avatar + meta tag + arrow CTA */}
        <div className="relative z-10 flex items-center gap-3 sm:gap-4" data-id="element-1491" data-mp="s7y3w239es7p">
          <div className="flex-shrink-0" data-id="element-1492" data-mp="x4evj69o3opj">
            <DjayAvatar size="md" emotion={primaryMeta.djayEmotion} variant={djayVariant} accessory={djayAccessory} tier={djayTier} animate data-id="element-1493" data-mp="ig1ye5yufztg" />
            
          </div>

          <div className="min-w-0 flex-1" data-id="element-1494" data-mp="nd21q94b6ogv">
            <div className="flex items-center gap-1.5" data-id="element-1495" data-mp="1b47fmiqov6oj">
              <Sparkles className="h-3.5 w-3.5 flex-shrink-0 text-yellow-200" data-id="element-1496" data-mp="1b2mlgu8adpr7" />
              <span className="truncate text-[11px] font-bold uppercase tracking-wider text-white/90" data-id="element-1497" data-mp="1fx0jz5i8wd03">
                Djay raadt aan · {primaryMeta.label}
              </span>
            </div>
          </div>

          <div className="flex-shrink-0" data-id="element-1498" data-mp="3jc480zizi8ia">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-md sm:h-14 sm:w-14" data-id="element-1499" data-mp="3ee2s2nguupb1">
              <ArrowRight className={`h-5 w-5 sm:h-6 sm:w-6 ${primaryColors.text}`} data-id="element-1500" data-mp="39gaylp0kav4q" />
              
            </div>
          </div>
        </div>

        {/* Title + tagline — full width, wraps freely on mobile */}
        <div className="relative z-10 mt-3 sm:mt-4" data-id="element-1501" data-mp="5zu5bw10ksqs">
          <h3 className="flex items-start gap-2 text-xl font-black leading-tight drop-shadow-sm sm:text-2xl" data-id="element-1502" data-mp="17j4mqyg92x6">
            <PrimaryIcon className="mt-0.5 h-5 w-5 flex-shrink-0 opacity-80 sm:h-6 sm:w-6" data-id="element-1503" data-mp="3sen8xe37mb1w" />
            <span className="break-words" data-id="element-1504" data-mp="ton2fvtdh6mz">{primary.module.title}</span>
          </h3>
          <p className="mt-1.5 text-sm font-medium leading-snug text-white/90 sm:text-base" data-id="element-1505" data-mp="oxdfj2z7khes">
            {primaryMeta.djayLine(primary)}
          </p>
        </div>
      </button>

      {/* ── Other choices: compact tiles ──────────────────────────────── */}
      {others.length > 0 && <div className="grid grid-cols-1 gap-px bg-slate-100 sm:grid-cols-2" data-id="element-1506" data-mp="119paah2c8j8y">
          {others.map(segment => {
        const meta = SEGMENT_META[segment.kind];
        const Icon = meta.icon;
        return <button key={segment.kind} type="button" onClick={() => onStart(segment.module.id)} className="flex items-center gap-3 bg-white px-4 py-3 text-left transition-colors hover:bg-slate-50 active:bg-slate-100" data-id="element-1507" data-mp="1b3pod8flkytt">
              
                <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${meta.accent}`} data-id="element-1508" data-mp="1owp1j3rwqgy7">
                
                  <Icon className="h-4 w-4" strokeWidth={2.4} data-id="element-1509" data-mp="1tvs6ypbsup6p" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-1510" data-mp="1ywfyrd7ura1z">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-1511" data-mp="23uhd5kuc2l74">
                    {meta.label}
                  </span>
                  <span className="block truncate text-sm font-bold text-slate-800" data-id="element-1512" data-mp="1e6u1a516slba">
                    {segment.module.title}
                  </span>
                  <span className="block truncate text-xs text-slate-500" data-id="element-1513" data-mp="1nvvl66aurmuj">
                    {segment.subtitle}
                  </span>
                </span>
                <ArrowRight className="h-4 w-4 flex-shrink-0 text-slate-300" data-id="element-1514" data-mp="1j3c4v1ks6dm1" />
              </button>;
      })}
        </div>}
    </motion.div>;
}